import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchDeleteNews, fetchDetailNewsById, fetchLikeNews, fetchSaveUnSaveNews, fetchUnlikeNews, isNewsLikedByUser, likeUnlikeNews, saveUnSaveNews, setViewNews } from '../../Reducers/actions/newsActions';
import './ViewNews.scss'
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/Icon/Icon';
import MediaPlayer from '../../components/MediaPlayer/MediaPlayer';
import ContributionSection from '../../components/ContributionSection/ContributionSection';
import ContributionSuccess from '../../components/ContributionSection/ContributionSuccess';
import CommentSection from '../../components/CommentSection/CommentSection';
import ReportCreationSheet from '../../components/ReportCreationSheet/ReportCreationSheet';
import ProfilePicture from '../../components/ProfilePicture/ProfilePicture';
import { getFirstTitleFromHtml, getFirstParagraphText, calculateReadingTime, formatTimeAgo, removeTitleFromHtml, htmlToPlainTextServer } from '../../../helpers/utilities';
import { fetchSubscribe } from '../../Reducers/actions/creationsActions';
import EditNews from '../../components/EditNews/EditNews';
import DeleteNews from '../../components/DeleteNews/DeleteNews';
import { handleGoBackRedirection } from '../../components/Layout/Layout';
import { usePreviousLocation } from '../../../helpers/hooks';
import SEOHead from '../../components/SEO/SEOHead';
import { useScrollPosition } from '../../../hooks/useScrollPosition';
import AboutCreator from '../../components/AboutCreator/AboutCreator';
import BioPreview from '../../components/BioPreview/BioPreview';
import Unsubscribe from '../../components/Unsubscribe/Unsubscribe';
import NotFound from '../NotFound/NotFound';
import MetaTags from '../../components/MetaTags/MetaTags';
import { getOGImageUrl } from '../../../helpers/getOGImageUrl';
import { getProfilePublicPath } from '../../../helpers/profilePaths';
import { getUserDisplayName } from '../../../helpers/userDisplayName';
import { useAutoContributePrompt } from '../../hooks/useAutoContributePrompt';
import ViewCreationSkeleton from '../ViewCreation/ViewCreationSkeleton';
import { useSoftwall } from '../SoftWall/SoftWall';
import { invalidateViewerNewsCache } from '../../../helpers/cache';

// Viewer the news detail was last shown to. Module scope so it survives unmounts:
// login/logout happens on other routes, and the detail's is_liked is per viewer (HYK-1111).
let newsDetailViewerId;


const ViewNews = () => {
  const view_news = useSelector(state => state.news.view_news)

  const { loading, user, auth_user_id, error } = useSelector(state => ({
    loading: state.news.loading,
    user: state.auth.user,
    auth_user_id: state.auth.user?.user_id,
    error: state.news.error,
  }))
  const sw = useSoftwall();
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { news_id } = useParams()

  // Safety check for softwall context
  if (!sw) {
    console.error('Softwall context is null - component may not be wrapped with SoftwallProvider');
  }

  const [showReport, setShowReport] = useState(false)
  const [showComments, setShowComments] = useState(false)
  const [showContribution, setShowContribution] = useState(false)
  const autoContributePrompt = useAutoContributePrompt(auth_user_id, view_news?.users?.user_id, showContribution);
  const [showSuccessContribution, setShowSuccessContribution] = useState(searchParams.get('success') === 'true')
  const [openNewsMenu, setOpenNewsMenu] = useState(false)
  const [openDeleteNews, setOpenDeleteNews] = useState(false)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
  const [showFullBio, setShowFullBio] = useState(false)
  const [showUnsubscribe, setShowUnsubscribe] = useState(false)
  const previousPath = usePreviousLocation();
  const [isSubscribed, setIsSubscribed] = useState(false)

  useEffect(() => {
    if (view_news?.users?.subscribers && auth_user_id) {
      const subscribed = view_news.users.subscribers.some(sub => sub.user_id === auth_user_id);
      setIsSubscribed(subscribed);
    }
  }, [view_news?.users?.subscribers, auth_user_id]);
  // Use scroll position hook to save current position when viewing news
  const { saveCurrentPosition } = useScrollPosition('news', 'main');

  const fromProfile = searchParams.get("from_profile") === "true" || view_news?.users?.user_id === auth_user_id;

  // Viewer changed (login/logout/account switch): drop the cached per-viewer detail,
  // otherwise is_liked stays the previous viewer's (HYK-1111). Runs before the fetch
  // effect below, which fetches on mount; while mounted, refetch here instead.
  const viewerId = auth_user_id ?? null;
  const isMountingRef = useRef(true);
  useEffect(() => {
    const isMounting = isMountingRef.current;
    isMountingRef.current = false;
    const viewerChanged = newsDetailViewerId !== undefined && newsDetailViewerId !== viewerId;
    newsDetailViewerId = viewerId;
    if (!viewerChanged) return;
    invalidateViewerNewsCache();
    if (!isMounting && news_id) {
      dispatch(fetchDetailNewsById(news_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerId]);

  useEffect(() => {
    // Reset loading state immediately when component mounts
    dispatch(setViewNews(null))
    setHasInitiallyLoaded(false)

    if (news_id) {
      dispatch(fetchDetailNewsById(news_id))
    }

    // Save current scroll position when viewing news
    // Note: saveCurrentPosition is called but not included in dependencies
    // to prevent infinite loops caused by unstable function reference
    saveCurrentPosition();

    return () => {
      // Clear view_news state to prevent stale data on navigation
      dispatch(setViewNews(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, news_id])

  // Track when we've initially loaded the news
  useEffect(() => {
    if (view_news && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [view_news, hasInitiallyLoaded])

  // Additional cleanup effect to handle navigation and prevent infinite loading
  useEffect(() => {
    return () => {
      // Reset loading state flags when component unmounts to prevent 
      // infinite loading when returning from detailed news view
      setHasInitiallyLoaded(false)
    }
  }, [])

  const handleContribute = async () => {
    setShowContribution(true)
  }

  const handleLikeUnlike = () => {
    if (!sw?.requireAuth("Like/Unlike News", "Sign up or log in to like this news")) {
      return;
    }
    if (!user?.user_id) return;
    const like = !isNewsLikedByUser(view_news, user.user_id)
    const apiCall = like ? fetchLikeNews : fetchUnlikeNews
    apiCall(view_news.id).then(() => {
    }).catch(err => {
      console.error('Error liking/unliking news:', err);
    });
    dispatch(likeUnlikeNews(view_news.id, like, user.user_id));

  }

  const handleNativeShare = async (news) => {
    if (navigator.share) {
      try {
        // Extract meaningful title from HTML content
        const newsTitle = getFirstTitleFromHtml(news.content) || 'News Article';
        const shareTitle = `Check out "${newsTitle}"`;

        await navigator.share({
          title: shareTitle,
          url: `${window.location.origin}/news/${news.id}`
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      alert("Sharing is not supported on this device.");
    }
  }

  const handleSaveUnsave = () => {
    if (!sw?.requireAuth("save/Unsave News", "Sign up or log in to save this news")) {
      return;
    }
    if (!auth_user_id) return;
    const unsave = view_news?.is_saved?.some(saved => saved.user_id === auth_user_id);

    fetchSaveUnSaveNews(view_news.id, unsave).then(() => {
    })
      .catch(err => {
        console.error('Error saving/unsaving: ', err);
      });
    dispatch(saveUnSaveNews(view_news.id, !unsave, auth_user_id));
  }

  const handleSubscribeUnsubscribe = (subscribed_to_id, subscribe = true, user) => {
    if (!sw?.requireAuth("Subscribe to News", "Sign up or log in to subscribe to this creator")) {
      return;
    }

    if (isSubscribed) {
      setShowUnsubscribe(true);
      return;
    }
    setIsSubscribed(true)
    dispatch({ type: 'SUBSCRIBE_SUCCESS', payload: { subscribe, subscriber_id: auth_user_id, subscribed_to_id, user } })

    // HYK-1109: on a real (non-optimistic) new-subscribe success, auto-open the Contribute
    // sheet pre-targeted to this artist. Never gates or blocks the subscribe action itself.
    const subscribeAttempt = autoContributePrompt.beginSubscribeAttempt();
    dispatch(fetchSubscribe(subscribed_to_id, subscribe)).then(({ success, alreadySubscribed }) => {
      if (autoContributePrompt.evaluate(subscribed_to_id, { success, eligible: !alreadySubscribed, startedAtGeneration: subscribeAttempt })) {
        setShowContribution(true);
      }
    })
  }

  const handleUnsubscribe = (subscribed_to_id, user) => {
    console.log('handleUnsubscribe', subscribed_to_id, user)
    // HYK-1109: invalidate any pending subscribe attempt so a stale success can't reopen
    // the Contribute sheet for the creator we just unsubscribed from.
    autoContributePrompt.cancel();
    dispatch(fetchSubscribe(subscribed_to_id, false))
    dispatch({ type: 'SUBSCRIBE_SUCCESS', payload: { subscribe: false, subscriber_id: auth_user_id, subscribed_to_id, user } })
    setIsSubscribed(false);
    setShowUnsubscribe(false);
  }
  const clickComment = () => {
    if (!sw?.requireAuth("Comment on News", "Sign up or log in to be able to comment on this news")) {
      return;
    }
    setShowComments(true)
  }

  const handleDeleteNews = () => {
    fetchDeleteNews(view_news.id).then(() => {
      dispatch({ type: 'DELETE_NEWS', payload: { news_id: view_news.id } });
      setOpenDeleteNews(false);
      setOpenNewsMenu(false);
      navigate(-1);
    }).catch((err) => {
      console.error("Error deleting creation:", err);
    });
  }

  const handleOpenEditNews = () => {
    navigate(`/news/${view_news.id}/edit`)
  }

  const reported = !(!view_news?.user_report || view_news?.user_report?.status === 'rejected')

  // Improved loading condition to prevent infinite loading
  // Show loading only when we're actually fetching data and haven't loaded the news yet


  // Show 404 page if there's an error
  if (error && !loading) {
    return (
      <NotFound
        label="News Article Not Found"
        subtitle="This news article doesn't exist or has been removed."
      />
    )
  }

  // If we have no news data and we're not in a loading state,
  // it means there was an error or the news doesn't exist
  if (!view_news && !loading && hasInitiallyLoaded) {
    return (
      <NotFound
        label="News Article Not Found"
        subtitle="This news article doesn't exist or has been removed."
      />
    )
  }



  // Generate SEO data for the news article
  const generateNewsSEOData = () => {
    if (!view_news) return {};

    const authorName = getUserDisplayName(view_news.users);
    const authorUsername = view_news.users?.userName
      ? (view_news.users.userName.startsWith('@') ? view_news.users.userName : `@${view_news.users.userName}`)
      : '';

    // Extract title from HTML content or use a default
    const newsTitle = getFirstTitleFromHtml(view_news.content) || 'News Article';

    // Generate page title
    const title = `${newsTitle} by ${authorName || authorUsername} - HYK`;

    // Extract description from first paragraph or create default
    const extractedDescription = getFirstParagraphText(view_news.content);
    const description = extractedDescription
      ? extractedDescription.length > 160
        ? extractedDescription.substring(0, 157) + '...'
        : extractedDescription
      : `Read this article by ${authorName || authorUsername} on HYK - Your creative community platform`;

    // Get featured image from news or author profile picture
    const image = view_news.media_url || view_news.users?.profilePicture || '/hyk_logo.png';

    // Generate current URL using the news ID
    const currentUrl = `${window.location.origin}/news/${view_news.id}`;

    return {
      title,
      description,
      image,
      url: currentUrl,
      type: 'article'
    };
  };
  const is_saved = view_news?.is_saved?.some(saved => saved.user_id === auth_user_id);
  // Gate on view_news only: a background refetch (e.g. auth resolving after a direct open)
  // must not swap already-loaded content for the skeleton (HYK-1111).
  return (view_news ?
    <div className='new-container'>
      {/* <SEOHead {...generateNewsSEOData()} /> */}
      {/* {openNewsMenu && <EditNews
        news={view_news}
        onClose={() => setOpenNewsMenu(false)}
        onDelete={() => setOpenDeleteNews(true)}
        onEdit={() => handleOpenEditNews()}
      />} */}
      {openDeleteNews && <DeleteNews
        news={view_news}
        onClose={() => setOpenDeleteNews(false)}
        onDelete={handleDeleteNews}
      />}
      {showContribution && <ContributionSection
        creation={{
          creation_id: null,
          title: `Creator ${view_news?.users?.userName}`,
          description: "Contribution to a creator",
          creator_id: view_news?.users?.user_id,
          thumbnail_url: view_news?.users?.profilePicture,
          users: view_news?.users
        }}
        onClose={() => {
          autoContributePrompt.handleClose();
          setShowContribution(false)
        }}
      />}
      {showSuccessContribution && <ContributionSuccess onClose={() => {
        setShowSuccessContribution(false)
        searchParams.delete("success")
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      }}
      />}
      {showComments && <CommentSection
        creation_id={view_news.id}
        type="news"
        onClose={() => setShowComments(false)}
      />}
      {showReport && <ReportCreationSheet
        onClose={() => setShowReport(false)}
        creation_id={view_news.id}
        type="news"
      />}
      {showFullBio && <AboutCreator user={view_news.users} onClose={() => setShowFullBio(false)} />}
      {showUnsubscribe && <Unsubscribe user={view_news.users} onClose={() => setShowUnsubscribe(false)} onSubmit={() => handleUnsubscribe(view_news.users?.user_id, view_news.users)} />}
      <MetaTags
        title={`${view_news.title} by ${view_news.users?.userName} - HYK`}
        description={htmlToPlainTextServer(view_news.content) || "Check out this great news on HYK."}
        image={getOGImageUrl('news', {
          title: view_news.title || 'HYK News',
          author: view_news.users?.userName || getUserDisplayName(view_news.users) || '',
          image: view_news.media_url || '',
          description: htmlToPlainTextServer(view_news.content) || '',
          id: view_news.id || ''
        })}
        url={`${window.location.origin}/news/${view_news.id}`}
        type="article"
      />
      <div className="header-news d-flex f-row">
        <span className="d-flex" onClick={() => handleGoBackRedirection(navigate, previousPath)}>
          <Icon alias={"chevron-left"} stroke={"#0A0F1E"} color="#0A0F1E" width={24} />
        </span>
        <span className='fg-dark fw-bold ml-12 tt-uppercase'>{view_news.users?.userName || getUserDisplayName(view_news.users)}</span>
        {fromProfile && <span onClick={handleOpenEditNews} className='ml-auto bg-bg-grey icon-container mr-16'><Icon alias={"edit_v2"} stroke="#FF3232" color='none' width={24} /></span>}
        <span className={`d-flex p-relative ${!fromProfile ? 'ml-auto' : ''}`} onClick={() => setOpenNewsMenu(!openNewsMenu)}>
          <span className='bg-bg-grey icon-container'>
            <Icon alias={"three_dots"} strokeWidth={2} stroke="#FF3232" width={24} />
          </span>
          {/* News Menu Dropdown */}
          {openNewsMenu && (
            <div className="news-menu-dropdown" >
              {/* <div className="news-menu-overlay" onClick={() => setOpenNewsMenu(false)}></div> */}
              <div className="news-menu-content">
                <div className="news-menu-item" onClick={() => handleNativeShare(view_news)}>
                  <span>Share News</span>
                  <Icon alias={"share"} stroke="#282828" color='none' width={20} />
                </div>
                <div className="news-menu-divider"></div>
                <div className="news-menu-item" onClick={() => (!reported ? setShowReport(true) : {})}>
                  <span className={`${!reported ? 'fg-black' : 'fg-grey7'}`}>{!reported ? "Report" : "Reported"}</span>
                  <Icon alias={"report_v2"} stroke={reported ? "#777" : "#0A0F1E"} color='none' width={20} />
                </div>
                {fromProfile && <Fragment>
                  <div className="news-menu-divider" style={{ height: '4px' }}></div>
                  <div className="news-menu-item" onClick={() => setOpenDeleteNews(true)}>
                    <span>Delete news</span>
                    <Icon alias={"trash_v2"} stroke="#0A0F1E" color='none' width={20} />
                  </div>
                </Fragment>}
              </div>
            </div>
          )}
        </span>


        {/* {!fromProfile ? (
          <span className="d-flex ml-auto" onClick={() => (!view_news.user_report || view_news.user_report?.status === 'rejected' ? setShowReport(true) : {})}>
            {!view_news.user_report || view_news.user_report?.status === 'rejected' ? (
              <Icon alias={"report"} color="#777777" width={24} className={"ml-24"} />
            ) : (
              <Icon alias={"unreport"} color="#777777" width={24} className={"ml-24"} />
            )}
          </span>
        ) : (
          <Icon alias={"menu"} width={24} color="#777777" onClick={() => setOpenNewsMenu(true)} className={'ml-8'} />
        )} */}
      </div>
      <div className={`thumbnail-news`}>
        {view_news?.media_url ?
          <MediaPlayer
            mediaUrl={view_news.media_url}
          />
          :
          <img
            src={view_news.media_url}
            alt={view_news.title || "Creation preview"}
            className="img-thumbnail"
          />
        }
      </div>
      <div className="interact-news d-flex f-row aic mb-8">
        <span className="d-flex" onClick={() => handleLikeUnlike()}>{!isNewsLikedByUser(view_news, user?.user_id) ? <Icon alias={"like"} color="#0A0F1E" stroke={"#0A0F1E"} width={24} /> : <Icon alias={"liked_v2"} width={24} height={24} color='#FF8CDC' />}</span>
        <p className="fs-xxs fw-medium-bold fg-crea ml-8 my-0">{view_news?.total_likes || ''}</p>
        <span className="d-flex" onClick={clickComment}><Icon alias={"comment"} stroke="#0A0F1E" color='none' width={24} className={"ml-16"} /></span>
        <p className="fs-xxs fw-medium-bold fg-crea ml-8 my-0">{view_news?.total_comments || ''}</p>
        <span className="d-flex ml-16" onClick={handleSaveUnsave}>
          {!is_saved ?
            <Icon alias={"star"} strokeWidth={2} color="#0A0F1E" width={24} />
            : <Icon alias={"saved_star"} strokeWidth={2} stroke='#FF8CDC' color='#FF8CDC' width={24} />}
        </span>
        <span className="d-flex" onClick={() => handleNativeShare(view_news)}><Icon alias={"share"} stroke="#0A0F1E" color='none' width={24} className={"ml-24"} /></span>

        <button onClick={handleContribute} className="contribute-btn btn-sm btn-xs red-outline-btn d-flex aic ml-auto fs-tiny fw-medium">
          <Icon alias={"credit_card"} color="#FF3232" stroke={'#FF3232'} width={16} className={'mr-4'} />
          <span className='fs-tiny fw-extra-bold'>Contribute</span>
        </button>
      </div>

      <div className='news-content px-16 mb-12'>
        <div className='d-flex aifs f-column profile-card'>
          <div className='d-flex aic w-100'>
            <div style={{ width: '40px', height: '40px !important' }}>

              <ProfilePicture imgUrl={view_news.users?.profilePicture} width={40} height={40} no_edit={true} handleClick={() => {
                const p = getProfilePublicPath(view_news.users);
                if (p) navigate(p);
              }} />
            </div>
            <div className='ml-12' onClick={() => {
              const p = getProfilePublicPath(view_news.users);
              if (p) navigate(p);
            }}>
              <p className='my-0 fs-regular fw-bold fg-black'>{getUserDisplayName(view_news.users) || view_news.users?.userName || ''}</p>

              {/* <p className='my-0 fs-xs fg-hyk-medgrey'>{formatTimeAgo(view_news.created_at)}</p> */}
            </div>
            <button
              className={`subscribe-btn ml-auto ${!isSubscribed
                ? "unsubscribed"
                : "subscribed"
                }`}
              onClick={() => isSubscribed ? setShowUnsubscribe(true) : handleSubscribeUnsubscribe(view_news.users?.user_id, true)}
            >
              {isSubscribed ? 'SUBSCRIBED' : 'SUBSCRIBE'}

            </button>
          </div>
          <div className='profile-bio'>
            <div className='my-0 fs-real fw-medium fg-black bio-text-wrap'>
              <BioPreview text={view_news.users?.bio} onMore={() => setShowFullBio(true)} />
            </div>
          </div>
        </div>
      </div>
      <h2 className='ml-16 mb-0 fg-crea fw-medium-bold mr-16' >{getFirstTitleFromHtml(view_news.content)}</h2>
      <p className='ml-16 fs-real fw-medium fg-grey7 my-0  d-flex aic mt-8 mb-16'>{calculateReadingTime(view_news.content)} min read <span className='dot'></span> {formatTimeAgo(view_news.created_at)}</p>

      <div className='px-16 news-content' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`${removeTitleFromHtml(view_news.content)}<br/>`) }} />
    </div> : <div className='new-container'>
      <div className="header-news d-flex f-row">
        <span className="d-flex" onClick={() => handleGoBackRedirection(navigate, previousPath)}>
          <Icon alias={"chevron-left"} stroke={"#0A0F1E"} color="#0A0F1E" width={24} />
        </span>
        <span className='fg-dark fw-bold ml-12 tt-uppercase'>{''}</span>
        <span onClick={handleOpenEditNews} className='ml-auto bg-bg-grey icon-container mr-16'></span>
        <span className={`d-flex p-relative ${!fromProfile ? 'ml-auto' : ''}`} >
          <span className='bg-bg-grey icon-container'>
            <Icon alias={"three_dots"} strokeWidth={2} stroke="#FF3232" width={24} />
          </span>

        </span>


        {/* {!fromProfile ? (
          <span className="d-flex ml-auto" onClick={() => (!view_news.user_report || view_news.user_report?.status === 'rejected' ? setShowReport(true) : {})}>
            {!view_news.user_report || view_news.user_report?.status === 'rejected' ? (
              <Icon alias={"report"} color="#777777" width={24} className={"ml-24"} />
            ) : (
              <Icon alias={"unreport"} color="#777777" width={24} className={"ml-24"} />
            )}
          </span>
        ) : (
          <Icon alias={"menu"} width={24} color="#777777" onClick={() => setOpenNewsMenu(true)} className={'ml-8'} />
        )} */}
      </div>
      <ViewCreationSkeleton />
    </div>
  )
}

export default ViewNews;